/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { useSelector } from "react-redux";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { Button } from "../../../components/ui/button";
import { Loader2 } from "lucide-react";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { constStr } from "../../../globals/constants";
import { basicDialogSelector, confirmDialogSelector, isLoadingStatusSelector } from "../../../store/helpers/helperSelectors";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import { convertSymbols } from "../../../utils/helpers";
import CustomAuditTemplateDrawer from "../../../components/Drawers/CustomAuditTemplateDrawer";
import RemoveTemplate from "../RemoveTemplate";
import ExportCustomAuditTemplate from "../ExportCustomAuditTemplate";
import CustomAuditTemplateImport from "../../../components/Tasks/CustomAuditTemplateImport";
import { GridEventListener, GridRowParams } from "@mui/x-data-grid";
import { useCustomAuditTemplateDisplay } from "./hooks";

const CustomAuditTemplates: React.FC = () => {
  const dispatch = useStoreDispatch();
  const [selectedTemplateRow, setSelectedTemplateRow] = React.useState<any | null>(null);
  const [isAttributesDrawerOpen, setIsAttributesDrawerOpen] = React.useState(false);
  const { data = [], isLoading, permissions, selectedTemplate, handleDeleteTemplate } = useCustomAuditTemplateDisplay();
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const isLoadingStatus = useSelector(isLoadingStatusSelector);

  const globalTemplates = (data || []).map((row: any, index: number) => ({
    id: row?.id ?? index,
    key: String(row?.id ?? index),
    templateName: row?.templateName || "",
    description: row?.description || "",
    imageConstraints: row?.imageConstraints || "",
    deviceType: row?.deviceType || "",
    goldStandard: false,
  }));

  const effectiveColumns = [
    {
      field: "templateName",
      headerName: "Template Name",
      flex: 1,
      renderCell: (cellValues: any) => convertSymbols(cellValues?.value || ""),
    },
    {
      field: "imageConstraints",
      headerName: "Image Constraints",
      flex: 1,
      renderCell: (cellValues: any) => convertSymbols(cellValues?.value || ""),
    },
    {
      field: "deviceType",
      headerName: "Device Type",
      flex: 1,
      renderCell: (cellValues: any) => convertSymbols(cellValues?.value || ""),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      renderCell: (cellValues: any) => convertSymbols(cellValues?.value || ""),
    },
    {
      field: "actions",
      type: "actions",
      width: 100,
      sortable: false,
      renderCell: (cellValues: any) => (
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <ExportCustomAuditTemplate selectedRow={cellValues?.row} />
          <RemoveTemplate selectedRow={cellValues?.row} />
        </div>
      ),
    },
  ];

  const handleClickTemplate = () => {
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.IMPORTTEMPLATEINTASK));
  };

  const handleDeleteTemplateAction = (actionName: string) => {
    if (!actionName || !selectedTemplate) {
      return;
    }
    handleDeleteTemplate({ template: selectedTemplate }, actionName);
  };

  const handleTemplateRowClick: GridEventListener<"rowClick"> = (params: GridRowParams<any>, event: any) => {
    if ((event?.target as HTMLElement)?.closest("button")) {
      return;
    }

    setSelectedTemplateRow(params?.row || null);
    setIsAttributesDrawerOpen(true);
  };

  return (
    <>
      {basicDialogOpenFor === DIALOGOPENFOR.IMPORTTEMPLATEINTASK && <CustomAuditTemplateImport modelOpenMode={"Add"} />}
      {!!selectedTemplateRow && (
        <CustomAuditTemplateDrawer
          open={isAttributesDrawerOpen}
          onClose={() => setIsAttributesDrawerOpen(false)}
          templateId={selectedTemplateRow.id ?? null}
          templateDisplayName={selectedTemplateRow.templateName || "Custom Audit Template"}
        />
      )}

      <div>
        {openConfirmDialog === DIALOGOPENFOR.DELETETEMPLATE && (
          <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteTemplateAction}>
            Are you sure you want to delete <strong>{`${selectedTemplate}` || "Unknown"}</strong>?
          </ConfirmDialog>
        )}
        <div className="flex items-center justify-end gap-2 text-right">
          {permissions?.hasOpCodeGroup("view_tasks") && permissions?.hasOpCodeGroup("manage_topology") && (
            <Button
              className="h-10 px-4 text-sm font-medium border border-input bg-background shadow-sm flex items-center gap-2 hover:bg-muted hover:text-accent-foreground transition-colors"
              variant="outline"
              onClick={handleClickTemplate}
              disabled={isLoadingStatus["importFile"]}
            >
              {isLoadingStatus["importFile"] ? <Loader2 className="h-4 w-4 animate-spin" /> : ""}
              Import Custom Audit Template
            </Button>
          )}
        </div>

        <div className="flex justify-between items-end mb-1 mt-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground pl-1">{`Showing ${globalTemplates?.length || 0} results`}</span>
          </div>
        </div>

        <CustomDataGrid
          tableId="customAuditTaskTemplate"
          sx={{ minHeight: "440px" }}
          rows={globalTemplates}
          columns={effectiveColumns}
          rowCount={globalTemplates?.length}
          loading={isLoading}
          disableColumnMenu={true}
          hideFooter={true}
          onRowClick={handleTemplateRowClick}
          sortingMode="client"
          pagination={false}
          fillContainer
          allowVerticalScroll
        />
      </div>
    </>
  );
};

export default CustomAuditTemplates;